import { useAuth0 } from "@/Contexts";
import { Terminal, useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { downloadExportFile } from "@/api/export";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { hasNonAllOptionActive } from "@/components/pageCards/filterSort/constants";
import {
  createFractionFilter,
  createTimeFilters,
} from "@/components/pageCards/filterSort/filterCreators";
import {
  CardFilterTimeFrameIso,
  TimeFilterCustomDate,
  getCustomTimeFrame,
  getTimeFrame,
} from "@/components/pageCards/filterSort/filterTimeFrame";
import { parseSearchParamsForFilterOptions } from "@/components/pageCards/filterSort/searchParams";
import {
  FilterSort,
  FilterSortMenuType,
} from "@/components/pageCards/filterSort/types";
import { useMultiSelectContext } from "@/components/pageCards/multiListTable/multiSelectContext";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { EVENT } from "@/pages/events/types";
import { useAccessParents } from "@/pages/infrastructure/functions";
import { useTrans } from "@/translations";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { SortingState } from "@tanstack/react-table";
import { TOptions } from "i18next";
import { uniq, upperCase } from "lodash";
import moment from "moment-timezone";
import {
  EmptyEventDto,
  ListEventsDtoItemsInner,
  UseEventDto,
} from "server/api/index.js";
import { EVENT_SORT_FIELDS } from "server/carrotApi/events/constants.js";
import { FilterTimeFrameValue } from "server/config-api/index.js";
import { ACCESS_PARENT } from "server/coreApi/accessParents/types.js";
import { MinimalCustomer } from "server/coreApi/customers/types.js";
import { IsoIdentityWithoutStatus } from "server/coreApi/identities/types.js";

import { timezoneStore, useConfig } from "../useConfig";
import { useSelectedSite } from "../useSelectedSite";
import {
  createAccessParentFilter,
  createContaminationFilter,
  createCustomerFilter,
  createDepotFilter,
  createEventTypeFilter,
  createResultCodeFilter,
  createTerminalFilter,
} from "./filters";

type EVENT_SORT_FIELDS_TYPE = (typeof EVENT_SORT_FIELDS)[number];

const sortFieldsMap = (key: string | undefined) => {
  switch (key) {
    case "timestamp":
      return "timestamp";
    case "weight_quantity":
      return "weight";
  }
  return undefined;
};

// Equivalent to ListEventsDtoItemsInner.TypeEnum on the server
export enum EVENT_TYPE {
  USE = "use",
  EMPTY = "empty",
}

type EventContainers = {
  accessParent: {
    id: string;
    name?: string;
    parentId?: string;
  };
  id: string;
  type: string;
  name?: string;
  isDepot?: boolean;
  depot?: {
    id: string;
    name: string;
  };
};

const mapEvent = (
  event: ListEventsDtoItemsInner,
  customerList: MinimalCustomer[],
  terminals: Terminal[],
  accessParents: ACCESS_PARENT[],
  t: (key: string, options?: TOptions) => string,
  allContainers: EventContainers[],
  timezone: string,
  identities?: IsoIdentityWithoutStatus[],
): EVENT => {
  let innerEvent: (UseEventDto | EmptyEventDto) & { type: string };

  // Cannot be a switch statement because TS won't recognize the type narrowing
  if (event.type === EVENT_TYPE.USE) {
    innerEvent = { ...event.useEvent, type: "Throw" };
  } else if (event.type === EVENT_TYPE.EMPTY) {
    innerEvent = { ...event.emptyEvent, type: "Empty" };
  } else {
    throw new Error("Unknown event type in useEvents.tsx.");
  }

  const {
    customer,
    point,
    result,
    identity,
    customerUnit,
    isContaminated,
    note,
    hasImages,
    wasteTypeIntended,
  } = innerEvent as Partial<UseEventDto>;

  let container;
  let accessParent:
    | {
        id: string;
        name?: string;
        parentId?: string;
      }
    | undefined;
  let depot;
  let terminal;

  const terminalWithEvent = terminals.find((t) => t.id === point?.id);
  if (terminalWithEvent) {
    terminal = terminalWithEvent;
  }

  const apWithEvent = accessParents.find((ap) => ap.id === point?.id);
  if (apWithEvent) {
    accessParent = apWithEvent;
    terminal = terminals.find((t) => t.id === apWithEvent.parentId);
  }

  if (!terminalWithEvent && !apWithEvent) {
    container = allContainers.find((c) => c.id === point?.id);

    accessParent = container?.accessParent;

    depot = (container?.isDepot && container) || container?.depot || null;

    terminal = terminals?.find(({ id }) => id === accessParent?.parentId);
  }

  const extraCustomerData = customerList?.find(({ id }) => id === customer?.id);

  const customerUnitData = extraCustomerData?.children?.find(
    ({ id }) => id === customerUnit?.id,
  );

  return {
    ...innerEvent,
    customer: {
      id: customer?.id,
      name: extraCustomerData?.name,
      crmId: extraCustomerData?.crmId,
    },
    customerUnit: {
      id: customerUnit?.id,
      name: customerUnitData?.name,
    },
    accessParent: {
      id: accessParent?.id,
      name: accessParent?.name,
    },
    terminal: terminal
      ? {
          id: terminal?.id,
          name: terminal?.name,
        }
      : undefined,
    depot: depot ? { id: depot?.id, name: depot?.name } : undefined,
    container: !container?.isDepot
      ? {
          id: point?.id,
          name: container?.name,
        }
      : undefined,
    identity: {
      id: identity?.id,
      ...identities?.find(({ id }: { id: string }) => id === identity?.id),
    },
    result,
    timestamp: moment(innerEvent.timestamp)
      .tz(timezone)
      .format("HH:mm YYYY-MM-DD"),
    type: t(`eventTypes:${upperCase(innerEvent.type)}`) ?? t("common:unknown"),
    contamination: {
      isContaminated,
      note,
      hasImages,
      wasteTypeIntended,
    },
  } as EVENT;
};

const mapEventId = (event: ListEventsDtoItemsInner) => {
  // Cannot be a switch statement because TS won't recognize the type narrowing
  if (event.type === EVENT_TYPE.USE) {
    return event.useEvent?.id;
  } else if (event.type === EVENT_TYPE.EMPTY) {
    return event.emptyEvent?.id;
  } else {
    throw new Error("Unknown event type in useEvents.tsx.");
  }
};

type EventFilters =
  | "timeframe"
  | "customerId"
  | "accessParentId"
  | "filterTerminalId"
  | "depotId"
  | "fractionId"
  | "resultCode"
  | "eventType"
  | "contamination";

const limit = 100;

export const useEvents = () => {
  const { t, language } = useTrans();
  const { isMWM, config } = useConfig();
  const [allSelected, setAllSelected] = useState(false);
  const { currentTerminal, terminals } = useTerminalsState();
  const { selectedEventIds, setSelectedEventIds } = useMultiSelectContext();
  const { wasteTypeConfig, terminalConfig } = useConfigService();
  const { getTokenSilently } = useAuth0()!;
  const { timezone } = timezoneStore();
  const { selectedSite } = useSelectedSite();

  const { setGlobalAlert } = useGlobalAlert();

  const [filterState, setFilterState] = useState<ActiveOptions<EventFilters>>(
    {} as ActiveOptions<any>,
  );
  const [showCustomerUnits, setShowCustomerUnits] = useState<boolean>(false);

  const defaultPeriod = useMemo(
    () => terminalConfig?.defaultTimeFrame || FilterTimeFrameValue.LastWeek,
    [terminalConfig],
  );

  const {
    timeframe: [timeframe] = [],
    customerId,
    accessParentId,
    filterTerminalId,
    depotId,
    fractionId,
    resultCode,
    eventType,
    contamination,
  } = filterState;

  const { startTimeISO, endTimeISO } = useMemo(() => {
    if (timeframe?.option === "interval.custom") {
      return getCustomTimeFrame(timeframe.value as TimeFilterCustomDate);
    } else {
      return getTimeFrame(
        timeframe?.value as FilterTimeFrameValue,
        defaultPeriod,
      );
    }
  }, [timeframe, defaultPeriod]);

  const location = useLocation();
  const [sortingState, setSortingState] = useState<SortingState>();

  // Support filtering by containerId without having a UI for it.
  // Used for linking to events from the container page.
  const containerId = useMemo(() => {
    const searchParams = location.search;
    const urlFilters = parseSearchParamsForFilterOptions(searchParams);
    return urlFilters?.["containerId"];
  }, [location.search]);

  // Filter by accessParentId, terminalId, or default to currentTerminal if not MWM
  const pointIds = useMemo(() => {
    if (containerId) {
      return [containerId];
    }
    if (hasNonAllOptionActive(depotId)) {
      return depotId.map(({ value }) => value);
    }
    if (hasNonAllOptionActive(accessParentId)) {
      return accessParentId.map(({ value }) => value);
    }
    if (isMWM && hasNonAllOptionActive(filterTerminalId)) {
      return filterTerminalId.map(({ value }) => value);
    }
    return isMWM ? undefined : [currentTerminal?.id];
  }, [
    currentTerminal?.id,
    isMWM,
    accessParentId,
    filterTerminalId,
    depotId,
    containerId,
  ]);

  const queryParams = useMemo(() => {
    const sortField = sortFieldsMap(sortingState?.[0]?.id);

    const eventTypes = isMWM
      ? hasNonAllOptionActive(eventType)
        ? [eventType[0].value as EVENT_TYPE]
        : undefined
      : [EVENT_TYPE.USE]; // contamination can only be queried on "use" events

    const isContaminated = hasNonAllOptionActive(contamination)
      ? contamination[0].value === "true"
      : undefined;

    return {
      startTime: startTimeISO,
      endTime: endTimeISO,
      pointIds,
      customerIds: hasNonAllOptionActive(customerId)
        ? customerId.map(({ value }) => value)
        : undefined,
      fractionCodes: hasNonAllOptionActive(fractionId)
        ? fractionId.map(({ value }) => value)
        : undefined,
      sortDirection: sortingState?.[0]?.desc
        ? "desc"
        : ("asc" as "asc" | "desc"),
      sortFields: sortField ? [sortField as EVENT_SORT_FIELDS_TYPE] : undefined,
      resultCode: hasNonAllOptionActive(resultCode)
        ? (resultCode[0].value as "accepted" | "rejected")
        : undefined,
      eventTypes,
      showCustomerUnits,
      isContaminated,
    };
  }, [
    sortingState,
    startTimeISO,
    endTimeISO,
    pointIds,
    customerId,
    fractionId,
    resultCode,
    eventType,
    showCustomerUnits,
    contamination,
    isMWM,
  ]);

  const {
    data: eventData,
    refetch: fetchEvents,
    isRefetching: isRefetchingEvents,
    isFetching: isLoadingEvents,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isError,
    error,
  } = trpc.events.getAll.useInfiniteQuery(
    { ...queryParams, limit },
    {
      getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
      enabled: !!(filterState && sortingState),
    },
  );

  useEffect(() => {
    if (!eventData) {
      return;
    }
    if (eventData.pages.length === 1) {
      setSelectedEventIds([]);
      return;
    }
    const items = eventData.pages.flatMap((page) => page.items);
    if (allSelected) {
      setSelectedEventIds(items.map((event) => mapEventId(event)));
    }
    // Only sync the selection when new pages arrive, not when allSelected toggles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData]);

  const { data: customerList = [], isLoading: isLoadingCustomers } =
    trpc.customers.getAllMinimal.useQuery({
      terminalId: isMWM ? undefined : currentTerminal?.id,
    });

  const hasCustomerUnits = useMemo(
    () => !!customerList.some((c) => c.children && c.children.length > 0),
    [customerList],
  );

  const { accessParents, isLoading: isLoadingPointList } = useAccessParents();

  const { wasteTypes } = useCommonEntitiesStore();

  const identityIds: string[] = useMemo(() => {
    if (!isMWM) {
      return [];
    }

    return uniq(
      eventData?.pages
        .flatMap((page) => page.items)
        .flatMap((event) =>
          event.type === EVENT_TYPE.USE
            ? event.useEvent?.identity?.id
            : undefined,
        )
        .filter((id) => id) ?? [],
    );
  }, [eventData, isMWM]) as string[];

  const { data: identities } = trpc.identities.getIdentitiesByIds.useQuery(
    {
      ids: identityIds,
    },
    {
      enabled: isMWM && !!identityIds.length,
    },
  );
  const allContainers = useMemo(
    () => [
      ...accessParents.flatMap(
        // all access parent containers
        (ap) =>
          ap.containers?.map((c) => ({
            ...c,
            accessParent: {
              id: ap.id,
              name: ap.name,
              parentId: ap.parentId,
            },
          })) || [],
      ),
      ...accessParents.flatMap(
        // all depots
        (ap) =>
          ap.depots?.map((d) => ({
            ...d,
            isDepot: true,
            type: "GROUP",
            accessParent: {
              id: ap.id,
              name: ap.name,
              parentId: ap.parentId,
            },
          })) || [],
      ),
      ...accessParents.flatMap(
        // all containers on depots
        (ap) =>
          ap.depots?.flatMap(
            (d) =>
              d.containers?.map((c) => ({
                ...c,
                accessParent: {
                  id: ap.id,
                  name: ap.name,
                  parentId: ap.parentId,
                },
                depot: {
                  id: d.id,
                  name: d.name,
                },
              })) || [],
          ) || [],
      ),
    ],
    [accessParents],
  );

  const joinedEventData: EVENT[] = useMemo(() => {
    let events: EVENT[] =
      eventData?.pages
        .flatMap((page) => page.items)
        .map((event) =>
          mapEvent(
            event,
            customerList,
            terminals,
            accessParents,
            t,
            allContainers,
            timezone,
            identities,
          ),
        ) ?? [];

    // only show the successful events for RE
    if (!isMWM) {
      events = events.filter((event) => event.result.code === "ACCEPTED");
    }

    return events;
  }, [
    eventData,
    customerList,
    terminals,
    accessParents,
    t,
    identities,
    allContainers,
    timezone,
    isMWM,
  ]);

  const areAllSelected = useMemo(() => {
    return joinedEventData.length === selectedEventIds.length;
  }, [joinedEventData, selectedEventIds]);

  const { data: wasteTypesTotal = [], isLoading: isLoadingWasteTypes } =
    trpc.terminals.getWasteTypes.useQuery({
      id: isMWM ? undefined : currentTerminal?.id,
    });

  const isLoadingFilters = useMemo(
    () => isLoadingCustomers || isLoadingPointList || isLoadingWasteTypes,
    [isLoadingCustomers, isLoadingPointList, isLoadingWasteTypes],
  );
  const isLoading = useMemo(
    () => isLoadingFilters || (isLoadingEvents && !isFetchingNextPage),
    [isLoadingEvents, isLoadingFilters, isFetchingNextPage],
  );

  useEffect(() => {
    if (!isLoadingFilters && !eventData) fetchEvents();
  }, [eventData, fetchEvents, isLoadingFilters, startTimeISO, endTimeISO]);

  const filteredAccessParents = useMemo(() => {
    if (hasNonAllOptionActive(filterTerminalId)) {
      return accessParents.filter(
        ({ parentId }) => parentId === filterTerminalId[0].value,
      );
    }
    return accessParents;
  }, [accessParents, filterTerminalId]);

  const filteredDepots = useMemo(() => {
    if (hasNonAllOptionActive(accessParentId)) {
      return filteredAccessParents.find(
        ({ id }) => id === accessParentId[0].value,
      )?.depots;
    }
    return filteredAccessParents.flatMap(({ depots }) => depots ?? []);
  }, [accessParentId, filteredAccessParents]);

  const exportFile = useCallback(
    async (type: "csv" | "excel") => {
      await downloadExportFile({
        type,
        entity: "events",
        queryParams,
        terminalId: isMWM ? undefined : currentTerminal?.id,
        getTokenSilently,
        language,
        t,
        showPeriodInTitle: true,
        selectedEventIds: areAllSelected ? undefined : selectedEventIds,
        selectedSite,
      });
    },
    [
      currentTerminal,
      queryParams,
      getTokenSilently,
      language,
      t,
      isMWM,
      selectedEventIds,
      areAllSelected,
      selectedSite,
    ],
  );

  const filters: FilterSort[] = useMemo(() => {
    return [
      createTimeFilters({
        id: "timeframe",
        menuType: FilterSortMenuType.TimeFrame,
        defaultValue: defaultPeriod,
      }),
      isMWM && createEventTypeFilter(),
      createCustomerFilter(customerList),
      isMWM && createTerminalFilter(terminals),
      createAccessParentFilter(filteredAccessParents),
      config?.useWasteSuctionSystem && createDepotFilter(filteredDepots),
      createFractionFilter(wasteTypesTotal, wasteTypes, wasteTypeConfig),
      isMWM && createResultCodeFilter(),
      !isMWM && createContaminationFilter(),
    ].filter(Boolean) as FilterSort[];
  }, [
    customerList,
    isMWM,
    terminals,
    filteredAccessParents,
    filteredDepots,
    wasteTypesTotal,
    wasteTypes,
    config,
    wasteTypeConfig,
    defaultPeriod,
  ]);

  const { mutate: updateEventMutation } = trpc.events.update.useMutation();

  const editEvent = useCallback(
    async ({
      id,
      weightKg,
      pointId,
      customerId,
    }: {
      id: string;
      weightKg?: number;
      pointId?: string;
      customerId?: string;
    }) => {
      await updateEventMutation(
        {
          id,
          weightKg,
          pointId,
          customerId,
        },
        {
          onSuccess: () => {
            setGlobalAlert({
              type: "success",
              message: "systemMessages:changesSaved",
            });
            fetchEvents();
          },
          onError: () => {
            setGlobalAlert({
              type: "error",
              message: "errors:failedSave",
            });
          },
        },
      );
    },
    [updateEventMutation, fetchEvents, setGlobalAlert],
  );

  const { mutate: deleteEventMutation } = trpc.events.delete.useMutation();

  const deleteEvent = useCallback(
    async ({ id }: { id: string }) => {
      await deleteEventMutation(
        {
          id,
        },
        {
          onSuccess: () => {
            setGlobalAlert({
              type: "success",
              message: "systemMessages:rowDeleted",
            });
            fetchEvents();
          },
          onError: () => {
            setGlobalAlert({
              type: "error",
              message: "errors:failedDelete",
            });
          },
        },
      );
    },
    [fetchEvents, deleteEventMutation, setGlobalAlert],
  );

  return {
    data: joinedEventData || [],
    filters,
    isLoading,
    isLoadingFilters,
    isError,
    error: error as Error | null | undefined,
    timeframe: { startTimeISO, endTimeISO } as CardFilterTimeFrameIso,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    sortingState,
    setSortingState,
    identities,
    selectedEventIds,
    setSelectedEventIds,
    filterState,
    setFilterState,
    setAllSelected,
    exportFile,
    startTimeISO,
    endTimeISO,
    areAllSelected,
    editEvent,
    deleteEvent,
    isRefetchingEvents,
    hasCustomerUnits,
    showCustomerUnits,
    setShowCustomerUnits,
  };
};
